import * as models from "../lib/api/models";
import ExpandableInputGroup from "./ExpandableInputGroup";
import LinedInput, {InputType} from "./LinedInput";
import React, {useRef} from "react";
import Select from "./Select";
import states from "../constants/states";
import useFocusInvalid from "../lib/useFocusInvalid";
import validateField from "../lib/validateApplicationField";

const strings = {
    addressLine1: "Street Address",
    addressLine1Extra:
        "We will use this to verify your identity as required by law. We recommend using the address on your driver's license, or if you cannot receive mail at that location, use the address where you receive bank statements, your cell phone bill, or similar.",
    addressLine2: "Apartment or suite #",
    city: "City",
    state: "State/Territory",
    zipcode: "ZIP Code",
    address: "Address",
    invalidAddress: "Address is invalid",
};

const STATE_STRINGS = states.map((s) => s.full);
const STATE_VALUES = states.map((s) => s.abbreviation);
const INVALID_ADDRESS = [strings.invalidAddress];

export const ALABAMA = "AL";
export const NEBRASKA = "NE";

function isAddressValid(address: models.ApplicationAddress): boolean {
    return (
        validateField(models.AddressKeys.AddressLine1, address[models.AddressKeys.AddressLine1])
            .isValid &&
        validateField(models.AddressKeys.City, address[models.AddressKeys.City]).isValid &&
        validateField(models.AddressKeys.State, address[models.AddressKeys.State]).isValid &&
        validateField(models.AddressKeys.Zipcode, address[models.AddressKeys.Zipcode]).isValid
    );
}

function getAddressValue1(ad1: string, ad2: string | null): string {
    let address = ad1;
    if (ad2) {
        address = address ? `${address} ${ad2}` : `${ad2}`;
    }
    return address;
}

function getAddressValue2(city: string, state: string, zipcode: string) {
    let address = city;
    if (state) {
        address = address ? `${address}, ${state}` : state;
    }
    if (zipcode) {
        address = address ? `${address} ${zipcode}` : zipcode;
    }
    return address;
}

type Props = {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    zipcode: string;
    onBlurInput: (event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
    onChangeAddress: (name: models.AddressKeys, value: string) => void;
    isExpanded: boolean;
    setIsExpanded: (expanded: boolean) => void;
    showLine1ExtraInfo?: boolean;
    firstInvalidKey?: string;
    validationCity?: string[];
    validationCount: number;
    validationLine1?: string[];
    validationLine2?: string[];
    validationState?: string[];
    validationZipcode?: string[];
};

export default function ExpandableAddress(props: Props) {
    const line1Ref = useRef<HTMLInputElement>(null);
    const line2Ref = useRef<HTMLInputElement>(null);
    const cityRef = useRef<HTMLInputElement>(null);
    const stateRef = useRef<HTMLSelectElement>(null);
    const zipcodeRef = useRef<HTMLInputElement>(null);

    const hasNameValidationMsg =
        props.validationLine1 ||
        props.validationLine2 ||
        props.validationCity ||
        props.validationState ||
        props.validationZipcode;

    const isValid =
        !hasNameValidationMsg &&
        isAddressValid({
            [models.AddressKeys.AddressLine1]: props.line1,
            [models.AddressKeys.AddressLine2]: props.line2,
            [models.AddressKeys.City]: props.city,
            [models.AddressKeys.State]: props.state,
            [models.AddressKeys.Zipcode]: props.zipcode,
        });

    const validationMessage = hasNameValidationMsg ? INVALID_ADDRESS : undefined;

    const value1 = getAddressValue1(props.line1, props.line2);
    const value2 = getAddressValue2(props.city, props.state, props.zipcode);

    let invalidRef: React.RefObject<HTMLSelectElement | HTMLInputElement> | undefined;
    if (props.firstInvalidKey === models.AddressKeys.AddressLine1) {
        invalidRef = line1Ref;
    } else if (props.firstInvalidKey === models.AddressKeys.AddressLine2) {
        invalidRef = line2Ref;
    } else if (props.firstInvalidKey === models.AddressKeys.City) {
        invalidRef = cityRef;
    } else if (props.firstInvalidKey === models.AddressKeys.State) {
        invalidRef = stateRef;
    } else if (props.firstInvalidKey === models.AddressKeys.Zipcode) {
        invalidRef = zipcodeRef;
    }

    useFocusInvalid(props.validationCount, invalidRef?.current);

    return (
        <ExpandableInputGroup
            focusFirst={() => line1Ref?.current?.focus()}
            isExpanded={props.isExpanded}
            isValid={isValid}
            name={strings.address}
            setIsExpanded={props.setIsExpanded}
            validationMessage={validationMessage}
            value={value1}
            valueLine2={value2}
        >
            {({onFocus, onBlur}) => (
                <>
                    <LinedInput
                        ref={line1Ref}
                        id={models.AddressKeys.AddressLine1}
                        name={models.AddressKeys.AddressLine1}
                        onFocus={onFocus}
                        onBlur={(e) => {
                            onBlur();
                            props.onBlurInput(e);
                        }}
                        label={strings.addressLine1}
                        type={InputType.Text}
                        value={props.line1}
                        setValue={(value) =>
                            props.onChangeAddress(models.AddressKeys.AddressLine1, value)
                        }
                        required={true}
                        maxLength={24}
                        validationMessage={props.validationLine1}
                        extraInfo={props.showLine1ExtraInfo ? strings.addressLine1Extra : undefined}
                    />
                    <LinedInput
                        ref={line2Ref}
                        id={models.AddressKeys.AddressLine2}
                        name={models.AddressKeys.AddressLine2}
                        label={strings.addressLine2}
                        onFocus={onFocus}
                        onBlur={(e) => {
                            onBlur();
                            props.onBlurInput(e);
                        }}
                        type={InputType.Text}
                        value={props.line2 ?? ""}
                        setValue={(value) => {
                            props.onChangeAddress(models.AddressKeys.AddressLine2, value);
                        }}
                        maxLength={24}
                        validationMessage={props.validationLine2}
                    />
                    <LinedInput
                        ref={cityRef}
                        id={models.AddressKeys.City}
                        name={models.AddressKeys.City}
                        onFocus={onFocus}
                        onBlur={(e) => {
                            onBlur();
                            props.onBlurInput(e);
                        }}
                        label={strings.city}
                        type={InputType.Text}
                        value={props.city}
                        setValue={(value) => {
                            props.onChangeAddress(models.AddressKeys.City, value);
                        }}
                        required={true}
                        maxLength={15}
                        validationMessage={props.validationCity}
                    />
                    <Select
                        ref={stateRef}
                        name={models.AddressKeys.State}
                        id={models.AddressKeys.State}
                        label={strings.state}
                        onFocus={onFocus}
                        onBlur={(e) => {
                            onBlur();
                            props.onBlurInput(e);
                        }}
                        options={STATE_STRINGS}
                        selectedIndex={STATE_VALUES.findIndex((v) => v === props.state)}
                        setSelected={(index) => {
                            props.onChangeAddress(models.AddressKeys.State, STATE_VALUES[index]);
                        }}
                        required={true}
                        validationMessage={props.validationState}
                    />
                    <LinedInput
                        ref={zipcodeRef}
                        id={models.AddressKeys.Zipcode}
                        name={models.AddressKeys.Zipcode}
                        onFocus={onFocus}
                        onBlur={(e) => {
                            onBlur();
                            props.onBlurInput(e);
                        }}
                        label={strings.zipcode}
                        type={InputType.Text}
                        value={props.zipcode}
                        setValue={(value) => {
                            props.onChangeAddress(models.AddressKeys.Zipcode, value);
                        }}
                        required={true}
                        maxLength={5}
                        minLength={5}
                        pattern="\d{5}"
                        validationMessage={props.validationZipcode}
                        inputMode="numeric"
                    />
                </>
            )}
        </ExpandableInputGroup>
    );
}
